import {apolloClient} from '../../../../../apolloClient';
import {
  type ConfigValuesAggregationQueryQuery,
  type ConfigValuesAggregationQueryQueryVariables,
  type ConfigValuesCategoriesQueryQuery,
  type ConfigValuesCategoriesQueryQueryVariables,
  type MetricStatisticsAggregationQueryQuery,
  type MetricStatisticsAggregationQueryQueryVariables,
} from '../../../../../generated/graphql';
import {EMPTY_FILTERS, toMongo} from '../../../../../util/filters';
import * as QueryTS from '../../../../../util/queryts';
import {type Sort} from '../../../../../util/queryTypes';
import {toSorted} from '../../../../../util/toSorted';
import {
  type SemanticLegendSettingsWithCategoricalConfig,
  YAxisValues,
} from '../../types';
import {
  CONFIG_VALUES_AGGREGATION_QUERY,
  CONFIG_VALUES_CATEGORIES_QUERY,
} from '../configValues.query';
import {METRIC_STATISTICS_AGGREGATION_QUERY} from '../metricStatistics.query';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from '../SemanticLegendDefaults';
import {type SemanticLegendPalette} from './colorPalettes';
import {getColorFromValue} from './getDefaultBucketColors';
import {safeNumber} from './numberNormalization';

export const MAX_NUMBER_OF_BUCKETS = 8;
export const MIN_NUMBER_OF_BUCKETS = 2;
export const MIN_NUMBER_OF_CATEGORIES = 1;

export function recalculateSemanticLegendBuckets(
  numberOfBuckets: number,
  metricStatistics: Record<string, {min: number; max: number}>,
  yValue: string,
  semanticLegendPalette: SemanticLegendPalette
) {
  if (!metricStatistics || !(yValue in metricStatistics)) {
    return [];
  }
  // This protects against the user switching from discrete (which has a min of 1) to continuous (which has a min of 2)
  const clampedNumberOfBuckets = Math.max(
    MIN_NUMBER_OF_BUCKETS,
    Math.min(numberOfBuckets, MAX_NUMBER_OF_BUCKETS)
  );

  const min = safeNumber(metricStatistics[yValue].min);
  const max = safeNumber(metricStatistics[yValue].max);
  const range = safeNumber(Math.abs(max - min));
  const bucketSize = range / clampedNumberOfBuckets;
  const newBuckets = Array.from({length: clampedNumberOfBuckets}, (_, i) => ({
    bucketMin: parseFloat((min + i * bucketSize).toFixed(6)),
    bucketMax: parseFloat((min + (i + 1) * bucketSize).toFixed(6)),
    color: getColorFromValue(i, clampedNumberOfBuckets, semanticLegendPalette),
  }));

  return newBuckets;
}

export async function recalculateSemanticLegendBucketsWithCategoricalConfig(
  semanticLegendSettingsData: SemanticLegendSettingsWithCategoricalConfig,
  filters: string,
  runsSort: Sort,
  entityName: string,
  projectName: string,
  palette: SemanticLegendPalette
) {
  switch (semanticLegendSettingsData.yAxisValue) {
    case YAxisValues.Config_Categories:
      semanticLegendSettingsData.selectedCategories =
        await recalculateSelectedCategories(
          semanticLegendSettingsData,
          filters,
          runsSort,
          entityName,
          projectName
        );
      semanticLegendSettingsData.buckets = undefined;
      break;
    case YAxisValues.Config_Number:
      semanticLegendSettingsData.buckets =
        await recalculateBucketsForConfigNumber(
          semanticLegendSettingsData,
          filters,
          runsSort,
          entityName,
          projectName,
          palette
        );
      semanticLegendSettingsData.selectedCategories = undefined;
      break;
    default:
      semanticLegendSettingsData.buckets = await recalculateBuckets(
        semanticLegendSettingsData,
        entityName,
        projectName,
        palette
      );
      semanticLegendSettingsData.selectedCategories = undefined;
      break;
  }
  semanticLegendSettingsData.shouldRecalculateBuckets = false;
}

async function recalculateSelectedCategories(
  semanticLegendSettingsData: SemanticLegendSettingsWithCategoricalConfig,
  filters: string,
  runsSort: Sort,
  entityName: string,
  projectName: string
) {
  const {data} = await apolloClient.query<
    ConfigValuesCategoriesQueryQuery,
    ConfigValuesCategoriesQueryQueryVariables
  >({
    query: CONFIG_VALUES_CATEGORIES_QUERY,
    variables: {
      filters,
      limit: 1000,
      order: QueryTS.sortToOrderString(runsSort),
      configKey: semanticLegendSettingsData.key?.replace('config.', '') ?? '', // this cannot be hit because the query will be skipped when `currentKey` doesn't exist
      configType: 'string',
      entityName,
      projectName,
    },
  });
  const configValuesCategories =
    (data?.project?.runs?.configValuesCategories as
      | Array<string>
      | undefined) ?? [];
  // Update the categories to be the total of categories or the number of categories specified by the user in the UI, whichever is smaller
  const maxIndex = Math.max(
    MIN_NUMBER_OF_CATEGORIES,
    Math.min(
      semanticLegendSettingsData?.numberOfBuckets ??
        DEFAULT_SEMANTIC_LEGEND_SETTINGS.numberOfBuckets,
      configValuesCategories.length,
      MAX_NUMBER_OF_BUCKETS
    )
  );
  return toSorted(configValuesCategories).slice(0, maxIndex);
}

async function recalculateBucketsForConfigNumber(
  semanticLegendSettingsData: SemanticLegendSettingsWithCategoricalConfig,
  filters: string,
  runsSort: Sort,
  entityName: string,
  projectName: string,
  palette: SemanticLegendPalette
) {
  const {data} = await apolloClient.query<
    ConfigValuesAggregationQueryQuery,
    ConfigValuesAggregationQueryQueryVariables
  >({
    query: CONFIG_VALUES_AGGREGATION_QUERY,
    variables: {
      filters,
      limit: 100,
      order: QueryTS.sortToOrderString(runsSort),
      configKey: semanticLegendSettingsData.key?.replace('config.', '') ?? '', // this cannot be hit because the query will be skipped when `currentKey` doesn't exist
      configType: 'number',
      entityName,
      projectName,
    },
  });
  return recalculateSemanticLegendBuckets(
    semanticLegendSettingsData?.numberOfBuckets ?? 0,
    {
      [YAxisValues.Config_Number]: {
        min: data?.project?.runs?.configValuesNumberAggregation?.Min ?? 0,
        max: data?.project?.runs?.configValuesNumberAggregation?.Max ?? 0,
      },
    },
    semanticLegendSettingsData?.yAxisValue ??
      DEFAULT_SEMANTIC_LEGEND_SETTINGS.yAxisValue,
    palette
  );
}

async function recalculateBuckets(
  semanticLegendSettingsData: SemanticLegendSettingsWithCategoricalConfig,
  entityName: string,
  projectName: string,
  palette: SemanticLegendPalette
) {
  const {data} = await apolloClient.query<
    MetricStatisticsAggregationQueryQuery,
    MetricStatisticsAggregationQueryQueryVariables
  >({
    query: METRIC_STATISTICS_AGGREGATION_QUERY,
    variables: {
      filters: JSON.stringify(toMongo(EMPTY_FILTERS)),
      limit: 10000,
      order: '-createdAt',
      metricName: semanticLegendSettingsData.key ?? '', // this cannot be hit because the query will be skipped when `currentKey` doesn't exist
      entityName,
      projectName,
    },
  });

  return recalculateSemanticLegendBuckets(
    semanticLegendSettingsData?.numberOfBuckets ?? 0,
    data?.project?.runs?.metricStatisticsAggregation ?? {},
    semanticLegendSettingsData?.yAxisValue ??
      DEFAULT_SEMANTIC_LEGEND_SETTINGS.yAxisValue,
    palette
  );
}
